import { useState, useCallback, useEffect, useRef } from "react"
import { EmployeePreferences, EmployeeProps } from "@/interfaces/employee_type"
import AxiosInstance from "@/components/Axios"
import { GetUserPreferencesFromAPI } from "@/api/settings"

/*
    This hook is used to manage the preferences of the user.
    This should be used for the settings page.

    Maybe I should remove the user parameter and have a function to set it.
    That way, I have a way to set preferences for a user that is not logged in. Like in the login page.
*/

const DEFAULT_PREFERENCES: EmployeePreferences = {
    darkMode: false,
    dashboard_layout: ["task", "project", "submittal", "rfi"]
}

/**
 * Type for preference keys to ensure type safety
 */
type PreferenceKey = keyof EmployeePreferences

/**
 * Hook for managing user preferences with local storage and API synchronization
 * @param user - The current user object
 * @returns Object containing preferences and methods to manage them
 */
export function usePreferences(user: EmployeeProps | null) {
    const [preferences, setPreferences] = useState<EmployeePreferences>(user?.preferences ?? DEFAULT_PREFERENCES)
    const [allPrefsForRole, setAllPrefsForRole] = useState<string[]>([]) // All available preferences for the role
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<Error | null>(null)
    const isInitialMount = useRef<boolean>(true)

    /**
     * Validates a specific preference key and its value
     * @param key - The preference key to validate
     * @param value - The value to validate
     * @returns boolean indicating if the preference is valid
     */
    const validatePreference = useCallback((key: PreferenceKey, value: unknown): boolean => {
        switch (key) {
            case "darkMode":
                return typeof value === "boolean"
            case "dashboard_layout":
                return Array.isArray(value) && value.every((item): item is string => typeof item === "string")
            default:
                return false
        }
    }, [])

    /**
     * Applies dark mode to the document
     * @param isDark - Whether dark mode should be enabled
     */
    const applyDarkMode = useCallback((isDark: boolean): void => {
        const root = document.documentElement
        if (!root.classList.contains("transition-colors")) {
            root.classList.add("transition-colors", "duration-300")
        }
        
        if (isDark) {
            root.classList.add("dark")
        } else {
            root.classList.remove("dark")
        }
    }, [])

    /**
     * Updates a specific preference
     * @param key - The preference key to update
     * @param value - The new value for the preference
     */
    const setPreference = useCallback((key: PreferenceKey, value: EmployeePreferences[PreferenceKey]): void => {
        if (!user) return

        if (!validatePreference(key, value)) {
            throw new Error(`Invalid preference value for key: ${key}`)
        }

        setPreferences(prev => {
            const newPreferences = { ...prev, [key]: value }
            if (key === "darkMode") {
                applyDarkMode(value as boolean)
            }
            return newPreferences
        })
    }, [validatePreference, applyDarkMode, user])

    /**
     * Saves preferences to both local storage and API
     */
    const savePreferences = useCallback(async (): Promise<void> => {
        if (!user) return

        try {
            localStorage.setItem("preferences", JSON.stringify(preferences))
            await AxiosInstance.post("/api/user/preferences", {
                email: user.email,
                preferences
            })
        } catch (err) {
            setError(err instanceof Error ? err : new Error("Failed to save preferences"))
            throw err
        }
    }, [preferences, user])

    /**
     * Gets the available preferences for the role
     * @returns The available preferences for the role
     */
    const getAvailablePreferences = useCallback((): string[] => {
        if (!allPrefsForRole) return []
        return allPrefsForRole
    }, [allPrefsForRole])

    /**
     * Fetches and applies user preferences
     */
    const getUserPreferences = useCallback(async (): Promise<void> => {
        if (!user) return

        setIsLoading(true)
        setError(null)

        try {
            // First, get available preferences from API to validate against
            const apiPreferences = await GetUserPreferencesFromAPI(user.email)
            const availablePrefs = apiPreferences?.available_preferences || []
            setAllPrefsForRole(availablePrefs)

            // Try local storage
            const localPrefs = localStorage.getItem("preferences")
            let currentPreferences = localPrefs ? JSON.parse(localPrefs) : null

            // Validate local preferences against available preferences
            if (currentPreferences) {
                const isValid = Object.entries(currentPreferences).every(([key, value]) => {
                    // First check if the preference key is in the available preferences
                    if (!availablePrefs.includes(key)) {
                        console.warn(`Preference key "${key}" not found in available preferences`)
                        return false
                    }
                    // Then validate the value
                    return validatePreference(key as PreferenceKey, value)
                })
                
                if (!isValid) {
                    console.warn("Local preferences validation failed, falling back to API")
                    currentPreferences = null
                }
            }

            // If no valid local preferences, use API preferences
            if (!currentPreferences && apiPreferences) {
                currentPreferences = apiPreferences.preferences
            }

            // Check if API is missing any preferences from default
            const mergedPreferences = { ...DEFAULT_PREFERENCES, ...(currentPreferences ?? {}) };
            if (JSON.stringify(mergedPreferences) !== JSON.stringify(currentPreferences)) {
                 currentPreferences = mergedPreferences;
            }

            // Apply final preferences
            const finalPreferences = currentPreferences ?? DEFAULT_PREFERENCES
            setPreferences(finalPreferences)
            applyDarkMode(finalPreferences.darkMode)

        } catch (err) {
            setError(err instanceof Error ? err : new Error("Failed to load preferences"))
        } finally {
            setIsLoading(false)
        }
    }, [user, validatePreference, applyDarkMode])

    // Initial load of preferences
    useEffect(() => {
        if (!user) {
            console.warn("No user found for preferences")
            return
        }
        
        if (isInitialMount.current) {
            isInitialMount.current = false
            getUserPreferences()
        }
    }, [getUserPreferences, user])

    return {
        preferences,
        allPrefsForRole,
        isLoading,
        error,
        setPreference,
        savePreferences,
        getUserPreferences,
        getAvailablePreferences,
    }
}

